import { DataVehicles, normalizeVouchers, PersonBenefits, VouchersCampaign } from '@esportal/portal-service';
import {
	HuchaDisclaimer1Text,
	HuchaDisclaimer2Plus10Text,
	HuchaDisclaimer2Plus15Text,
	HuchaDisclaimer2S10To15Text,
} from '../../components/benefits/hucha-bonos/hucha-venta/components/disclaimers/hucha-disclaimers';
import { getHuchaTierFromDiscounts } from '../../components/benefits/hucha-bonos/hucha-venta/helpers/get-renovador-tier';
import { NamedDisclaimer } from './disclaimers-utils';
import { noVehicleOrder2025, voucherAlertExists } from '../alert-utils';
import { BonoDisclaimerText } from '../../components/benefits/hucha-bonos/bono-posventa/components/disclaimers/bono-disclaimer-content';

export const IdWeChargeDisclaimerText =
	'Promoción aplicable a pedidos de clientes particulares de vehículos 100% eléctricos realizados entre el 01/10/2025 y el 31/12/2025, ambos inclusive. Al adquirir un modelo de la gama ID. de Volkswagen Turismos, los clientes particulares recibirán un bono de carga pública con un importe de 733€, que se podrá canjear únicamente en las estaciones de recarga que forman parte de WeCharge, el servicio de recarga disponible en la aplicación Volkswagen. Este bono de 733€ corresponde a una estimación orientativa del coste de 10.000 km de carga pública (calculado en base a un consumo WLTP homologado de 15,9 kWh/100 km para el modelo ID.4 Pro, recargando en la red IONITY a 0,37€/kWh mediante la tarifa WeCharge Plus; importe de 588,30€) más el importe equivalente a 12 mensualidades de dicha tarifa (11,99€/mes, total 143,88€). Esta estimación no es vinculante y puede variar según el uso real del vehículo, las condiciones de carga y las tarifas eléctricas aplicables. El bono puede utilizarse con cualquier suscripción activa de WeCharge. El cliente deberá abonar las mensualidades de la suscripción que elija, y el valor equivalente incluido en el bono podrá redimirse en forma de kWh de carga pública. El bono deberá activarse en un plazo máximo de tres meses desde la entrega del vehículo, y para ello el cliente deberá estar registrado en el Club Volkswagen a través del siguiente enlace: <a href="https://www.volkswagen.es/es/clientes/mi-club-volkswagen.html" style="color:#3C484D;">https://www.volkswagen.es/es/clientes/mi-club-volkswagen.html</a>. El importe del bono deberá consumirse antes del 31/12/2029. No canjeable por dinero en metálico.';

const getHuchaDisclaimers = (personBenefits: PersonBenefits | null): string[] => {
	const huchaTier = getHuchaTierFromDiscounts(personBenefits?.benefits.discounts ?? []);

	if (huchaTier === 'renovador10To15') {
		return [HuchaDisclaimer1Text, HuchaDisclaimer2S10To15Text];
	}

	if (huchaTier === 'renovadorPlus15') {
		return [HuchaDisclaimer1Text, HuchaDisclaimer2Plus15Text];
	}

	if (huchaTier === 'renovadorPlus10') {
		return [HuchaDisclaimer1Text, HuchaDisclaimer2Plus10Text];
	}

	return [HuchaDisclaimer1Text];
};

export const getAllDisclaimers = (
	personBenefits: PersonBenefits | null,
	vehicles: DataVehicles | null,
): NamedDisclaimer[] => {
	if (!vehicles || !personBenefits) return [];

	const normalizedVouchers = normalizeVouchers(personBenefits);

	const textToIndexMap = new Map();
	const disclaimers: NamedDisclaimer[] = [];
	let uniqueIndex = 1;

	let vouchersDisclaimers: string[] = [];
	let alertsDisclaimers: string[] = [];

	// add hucha disclaimers text
	const huchaDisclaimers = getHuchaDisclaimers(personBenefits);

	// add alerts disclaimers text
	if (noVehicleOrder2025(vehicles) && !voucherAlertExists(VouchersCampaign.IDWeChargePostPedido, normalizedVouchers)) {
		alertsDisclaimers.push(IdWeChargeDisclaimerText);
	}

	if (voucherAlertExists(VouchersCampaign.IDWeChargePostPedido, normalizedVouchers)) {
		alertsDisclaimers.push(IdWeChargeDisclaimerText);
	}

	// add bonus disclaimers text
	normalizedVouchers.forEach((voucher) => {
		if (!voucher.legalText?.trim()) return;

		const processedText = voucher.legalText
			.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
			.replace(/__(.*?)__/g, '<u>$1</u>')
			.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, label, url) => {
				const hostname = new URL(url).hostname.replace(/^www\./, '');
				const target = hostname === 'volkswagen.es' ? '' : ' target="_blank"';

				return `<a href="${url}"${target}>${label}</a>`;
			});
		vouchersDisclaimers.push(processedText);
	});

	const allDisclaimersText: string[] = [
		...vouchersDisclaimers,
		...alertsDisclaimers,
		...huchaDisclaimers,
		BonoDisclaimerText,
	];

	allDisclaimersText.forEach((text) => {
		if (!textToIndexMap.has(text)) {
			textToIndexMap.set(text, uniqueIndex);
			disclaimers.push({
				ref: `${uniqueIndex}.`,
				text,
			});
			uniqueIndex++;
		}
	});

	return disclaimers;
};
